/**
 * Reference-stable map features for the DPS scenario form.
 *
 * The form's `form.Subscribe` selector runs outside a React render, so it
 * cannot hold a `useMemo`, and `@tanstack/react-store` compares the selector's
 * whole result with `===`. A freshly built array therefore re-renders the map
 * subtree on every store emission — every keystroke in every field — and the
 * new `areas` reference re-runs `ScenarioMap`'s bbox memo and its `fitBounds`
 * effect, throwing away whatever pan or zoom the user had set up (INS-12060).
 *
 * Returning the memoized array *directly* from the selector is what fixes it:
 * the identity only moves when the set, order, or geometry of what the map
 * draws moves, which is exactly when a re-fit is wanted.
 */
import { createGeometryTokenTable } from 'utils/geometry-token-table';
import { createKeyedMemo } from 'utils/keyed-memo';
import type { AreaFeature } from './types';

type GeometryMap = Record<number, GeoJSON.Geometry>;

/** The only thing this module reads off a form area. */
interface MapAreaItem {
  id: number;
}

// Identity tokens for geometry objects, so the cache key moves when an area is
// re-pointed at a different geometry without serializing its coordinates.
//
// `utils/geometry-overlap` holds a second table. Sharing the *instance* would
// be actively wrong, not just coupled: `scenario-form.test.tsx` resets the
// overlap memo without resetting this one, so a shared table would re-issue
// token 1 under a still-live map key — a stale cache *hit*. Sharing the code
// via the factory costs nothing and keeps the instances apart.
const geometryTokens = createGeometryTokenTable();

/**
 * Fingerprints exactly what the map draws: which areas have a geometry, in
 * which order, and which geometry object each one currently points at.
 *
 * Unlike `overlapCacheKey`, this is deliberately NOT sorted. The features are
 * rendered in array order and Mapbox stacks the layers in that order, so a
 * reorder is a real change to the rendered result.
 * @param {ReadonlyArray<MapAreaItem>} areas - Area items from form state
 * @param {Record<number, GeoJSON.Geometry>} geometries - Map of id to geometry
 * @return {string} The cache key
 */
export function mapAreasCacheKey(
  areas: readonly MapAreaItem[],
  geometries: GeometryMap
): string {
  const parts: string[] = [];

  for (const area of areas) {
    const geometry = geometries[area.id];
    if (!geometry) continue;

    // Control characters as separators: neither can occur in a numeric id or a
    // token, so no two different feature lists can fingerprint alike.
    parts.push(`${area.id}\u0000${geometryTokens.token(geometry)}`);
  }

  return parts.join('\u0001');
}

const mapAreasMemo = createKeyedMemo<readonly AreaFeature[]>(
  'getMemoizedMapAreas'
);

/**
 * The map's features, as a reference that only changes when the rendered
 * geometry set does. Call this straight from the `form.Subscribe` selector —
 * wrapping the result in anything fresh gives the stable reference away.
 *
 * The returned array and its items are shared across calls, so callers must
 * not mutate them; hence the readonly type.
 *
 * One concurrent caller only. `createKeyedMemo` describes its single slot as
 * "slower, never wrong" under alternating keys, which holds for the overlap
 * memo because the *result* is the value. Here the *reference* is the value,
 * so two forms alternating keys would return a fresh array every call and
 * quietly reinstate INS-12060 with nothing failing. One mount today
 * (`page.tsx`), one importer (`scenario-form.tsx`) — keep it that way, or give
 * the second caller its own memo. `createKeyedMemo` warns in development if
 * that ever stops holding, so this is no longer comment-enforced alone.
 * @param {ReadonlyArray<MapAreaItem>} areas - Area items from form state
 * @param {Record<number, GeoJSON.Geometry>} geometries - Map of id to geometry
 * @return {ReadonlyArray<AreaFeature>} Features for the areas with a geometry
 */
export function getMemoizedMapAreas(
  areas: readonly MapAreaItem[],
  geometries: GeometryMap
): readonly AreaFeature[] {
  return mapAreasMemo.get(mapAreasCacheKey(areas, geometries), () => {
    // One lookup per area, mirroring `mapAreasCacheKey` above, so the two
    // cannot disagree about which areas count as drawable. The filter/map pair
    // this replaces looked each id up twice, and typed `geometry` as
    // non-optional only because `noUncheckedIndexedAccess` is off — the index
    // signature simply never admits `undefined`.
    const features: AreaFeature[] = [];

    for (const area of areas) {
      const geometry = geometries[area.id];
      if (!geometry) continue;

      features.push({ id: area.id, geometry });
    }

    return features;
  });
}

/** TEST-ONLY. Drops the memoized features and the geometry identity table. */
export function __resetMapAreasMemoForTest(): void {
  mapAreasMemo.reset();
  // The token table has to go too, or a geometry object carried over from a
  // previous test keeps a token that makes key assertions order-dependent.
  // This clears OUR table only — `geometry-overlap`'s is untouched, which is
  // what `scenario-form.test.tsx` relies on.
  geometryTokens.reset();
}
